import { API } from 'aws-amplify';
import { convert } from 'html-to-text';
import { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import useAlertFading from '../SurveyQuestionnaireDetailsConsole/hooks/useAlertFading';
import { checkUserGroup, getAuthToken, USER_GROUPS } from "../utils/AuthUtils";
import { DATA_PROVIDER_ALLOWED_STATUSES } from '../utils/ECSUtils';
import EditTrackingRecordModal from './EditTrackingRecordModal/EditTrackingRecordModal';
import ActionToolbar from './components/ActionToolbar';
import FilterPanel from './components/FilterPanel';
import StatusSummary from './components/StatusSummary';
import TrackingTable from './components/TrackingTable';
import './styles.scss';

export default function SurveyQuestionnaireTrackingConsole() {
    const location = useLocation();
    const navigate = useNavigate();
    const [trackingData, setTrackingData] = useState([]);
    const [error, setError] = useState("");
    const [displayError, setDisplayError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [processingStatus, setProcessingStatus] = useState("");
    const [sensitive, setSensitive] = useState(undefined);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState(new Set());
    const [selectedRows, setSelectedRows] = useState([]);
    const [displayEditRecordModal, setDisplayEditRecordModal] = useState(false);
    const [displayResults, setDisplayResults] = useState(false);
    const [exportingNotes, setExportingNotes] = useState(false);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [fadingAlerts, setFadingAlerts] = useState({
        displayError: false
    });

    useAlertFading(displayError, setDisplayError, 'displayError', setFadingAlerts);

    const fetchTrackingData = useCallback(async () => {
        try {
            setTrackingData([]);
            setError("");
            setDisplayError(false);
            setSelectedCompanyIds(new Set());
            setDisplayResults(false);
            setLoading(true);

            const authToken = await getAuthToken();
            const currentYear = new Date().getFullYear().toString();
            const path = `/survey-questionnaire-submission/records/${currentYear}`;

            const queryStringParameters = {};
            if (processingStatus) {
                queryStringParameters["processingStatus"] = processingStatus;
            }
            if (sensitive !== undefined) {
                queryStringParameters["sensitive"] = sensitive;
            }

            const response = await API.get('AnnualCompensations', path, {
                headers: {
                    'Content-Type': 'application/json',
                    'Token': authToken
                },
                queryStringParameters
            });

            setLoading(false);
            setTrackingData(response.results);
            setDisplayResults(true);
        } catch (error) {
            console.error(error);
            setLoading(false);
            setError("An error occurred retrieving questionnaire tracking data");
            setDisplayError(true);
        }
    }, [processingStatus, sensitive]);

    // Effect to set filter state from URL params and fetch data
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const status = searchParams.get('status');
        const sensitiveParam = searchParams.get('sensitive');

        // Reset states before updating
        setTrackingData([]);
        setError("");
        setDisplayError(false);
        setSelectedCompanyIds(new Set());
        setSelectedRows([]);
        setDisplayResults(false);
        setLoading(true);

        // Update filter states
        if (status) {
            setProcessingStatus(status);
        }
        if (sensitiveParam !== null) {
            setSensitive(sensitiveParam === 'true');
        }

        // Fetch data with current filter states
        const fetchData = async () => {
            try {
                const authToken = await getAuthToken();
                const currentYear = new Date().getFullYear().toString();
                const path = `/survey-questionnaire-submission/records/${currentYear}`;

                const queryStringParameters = {};
                if (status) {
                    queryStringParameters["processingStatus"] = status;
                }
                if (sensitiveParam !== null) {
                    queryStringParameters["sensitive"] = sensitiveParam === 'true';
                }

                const response = await API.get('AnnualCompensations', path, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Token': authToken
                    },
                    queryStringParameters
                });

                setTrackingData(response.results);
                setDisplayResults(true);
            } catch (error) {
                console.error(error);
                setError("An error occurred retrieving questionnaire tracking data");
                setDisplayError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [location.search]);

    const handleFilterChange = (newStatus, newSensitive) => {
        const searchParams = new URLSearchParams();
        if (newStatus) {
            searchParams.set('status', newStatus);
        }
        if (newSensitive !== undefined) {
            searchParams.set('sensitive', newSensitive);
        }
        const queryString = searchParams.toString();
        const newPath = '/survey-questionnaire-submission' + (queryString ? '?' + queryString : '');
        navigate(newPath, { replace: true });
    };

    const fetchQuestionnaireNotes = async (companyId) => {
        try {
            const authToken = await getAuthToken();
            const currentYear = new Date().getFullYear().toString();
            const path = `/survey-questionnaire-submission/notes/${currentYear}/${companyId}`;

            const response = await API.get('AnnualCompensations', path, {
                headers: {
                    'Content-Type': 'application/json',
                    'Token': authToken
                }
            });
            return response;
        } catch (error) {
            console.error(error);
            return { notes: [] };
        }
    };

    const formatDate = (unixTime) => {
        if (!unixTime || unixTime === undefined) return "";
        return new Date(Number(unixTime) * 1000).toLocaleString();
    };

    const formatNoteOutput = (notes) => {
        return notes.map(note => ({
            ...note,
            noteText: convert(note.noteText),
            createdOn: formatDate(note.createdOn),
            modifiedOn: formatDate(note.modifiedOn)
        }));
    };

    const handleExportNotes = async () => {
        if (selectedCompanyIds.size === 0) return;

        setExportingNotes(true);
        try {
            const XLSX = await import('xlsx');
            const wb = XLSX.utils.book_new();
            const allNotes = [];

            for (const companyId of selectedCompanyIds) {
                const response = await fetchQuestionnaireNotes(companyId);
                if (response?.notes) {
                    const notes = response.notes;
                    const companyName = trackingData.find((data) => data.companyId === companyId)?.participantName;
                    notes.forEach((note) => note.participantName = companyName);
                    allNotes.push(...notes);
                }
            }

            const formattedNotes = formatNoteOutput(allNotes);
            const ws = XLSX.utils.json_to_sheet(formattedNotes);
            XLSX.utils.book_append_sheet(wb, ws, "Questionnaire Notes");
            XLSX.writeFile(wb, 'Questionnaire_Notes.xlsx');
        } catch (error) {
            console.error(error);
            setError("An error occurred while exporting notes");
            setDisplayError(true);
        } finally {
            setExportingNotes(false);
        }
    };

    const handleEditRecords = () => {
        // Check if any selected records are in admin-only processing status
        if (!isAdminUser) {
            const unauthorizedRecords = trackingData.filter(
                (row) => selectedCompanyIds.has(row.companyId) &&
                    !DATA_PROVIDER_ALLOWED_STATUSES.includes(row.processingStatus)
            );

            if (unauthorizedRecords.length > 0) {
                setError("You are not authorized to edit records with the current processing status.");
                setDisplayError(true);
                return;
            }
        }

        const filteredRows = trackingData.filter((row) => selectedCompanyIds.has(row.companyId));
        setSelectedRows(filteredRows);
        setDisplayEditRecordModal(true);
    };

    const handleRowClick = async (row, col) => {
        if (col.key !== 'select-row') {
            try {
                setLoading(true);
                // Check if user is authorized to view this record
                if (!isAdminUser && !DATA_PROVIDER_ALLOWED_STATUSES.includes(row.processingStatus)) {
                    setError("You are not authorized to view this record due to its processing status.");
                    setDisplayError(true);
                    setLoading(false);
                    return;
                }

                const queryParams = new URLSearchParams();
                if (processingStatus) {
                    queryParams.set('status', processingStatus);
                }
                if (sensitive !== undefined) {
                    queryParams.set('sensitive', sensitive);
                }
                // Add required parameters
                queryParams.set('surveyYear', row.surveyYear);

                const queryString = queryParams.toString();
                const path = `/survey-questionnaire-submission/${row.companyId}/${row.participantId}?${queryString}`;
                navigate(path);
            } catch (error) {
                setLoading(false);
                console.error('Navigation error:', error);
                setError("Error navigating to questionnaire details");
                setDisplayError(true);
            }
        }
    };

    const handleCloseEditModal = () => {
        setDisplayEditRecordModal(false);
        fetchTrackingData();
    };

    // Determine if we should show the status overview section
    const shouldShowStatusSection = !processingStatus && (displayResults || loading);

    // Add effect to check admin status
    useEffect(() => {
        const checkAdminStatus = async () => {
            const isAdmin = await checkUserGroup(USER_GROUPS.ADMIN);
            setIsAdminUser(isAdmin);
        };
        checkAdminStatus();
    }, []);

    return (
        <div className="survey-questionnaire-tracking-container">
            <div className="position-relative">
                {displayError && (
                    <Alert
                        variant="danger"
                        dismissible
                        className={`status-alert ${fadingAlerts.displayError ? 'fade-out' : ''}`}
                        onClose={() => {
                            setFadingAlerts(prev => ({ ...prev, displayError: true }));
                            setTimeout(() => setDisplayError(false), 300);
                        }}
                    >
                        {error}
                    </Alert>
                )}
            </div>
            {loading && (
                <div className="loading-backdrop">
                    <Spinner animation="border" variant="primary" className="loading-spinner">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            )}
            <FilterPanel
                processingStatus={processingStatus}
                setProcessingStatus={(status) => {
                    setProcessingStatus(status);
                    handleFilterChange(status, sensitive);
                }}
                sensitive={sensitive}
                setSensitive={(sensitive) => {
                    setSensitive(sensitive);
                    handleFilterChange(processingStatus, sensitive);
                }}
            />
            {displayResults && trackingData?.length > 0 &&
                <div className="d-flex justify-content-start mb-3">
                    <div className="total-records">
                        Total Records: <span className="fw-bold">{trackingData.length}</span>
                    </div>
                </div>
            }

            {(displayResults || loading) && (
                <>
                    {shouldShowStatusSection && (
                        <StatusSummary data={trackingData} loading={loading} />
                    )}
                    <ActionToolbar
                        onExportNotes={handleExportNotes}
                        onEdit={handleEditRecords}
                        exportingNotes={exportingNotes}
                        selectedCompanyIds={selectedCompanyIds}
                        loading={loading}
                    />
                    <TrackingTable
                        data={trackingData}
                        selectedCompanyIds={selectedCompanyIds}
                        onRowSelection={setSelectedCompanyIds}
                        onRowClick={handleRowClick}
                    />
                </>
            )}
            {displayEditRecordModal && (
                <EditTrackingRecordModal
                    show={displayEditRecordModal}
                    onHide={handleCloseEditModal}
                    selectedRows={selectedRows}
                />
            )}
        </div>
    );
}